import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify'

// components
import { useTranslation } from 'react-i18next'
import GroupForm from '../../components/groupForm';
import ConfirmationDialog from '../../components/confirmationDialog'
import LoadingSpinner from '../../components/loadingSpinner';
import Breadcrumbs from '../../components/Breadcrumbs'

// hooks
import useGroup from '../../hooks/useGroup';
import useDialogVisibility from '../../hooks/useDialogVisibility'
import { useIsMobileView } from '../../utils/isMobileView'

// constants
import { EDIT_FORM_FIELDS_CONFIG } from './editGroup.constants';

// helpers
import { getInitialGroup, makeEditGroupDTO, areFormValuesChanged } from './editGroup.helpers';
import { removeEmptyProperties } from '../../helpers/general';

// services
import { updateGroup } from '../../services/groups';
import { validateGroup } from '../../components/groupForm/helpers/groupForm.validators';

function EditGroup() {
  const isMobileView = useIsMobileView()
  const { groupId } = useParams()
  const { group: groupObj, isGroupLoading } = useGroup(groupId);
  const [
    isCancelConfirmationDialogOpen,
    openCancelConfirmationDialog,
    closeCancelConfirmationDialog,
  ] = useDialogVisibility(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [group, setGroup] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (groupObj) {
      const initialGroup = getInitialGroup(groupObj);
      setGroup(initialGroup);
    }
  }, [groupObj])

  const handleUpdateGroupSuccess = () => {
    setErrors({});
    toast.success(t('group-updated-success'))
    navigate(`/view-group/${groupId}`)
  };

  const handleUpdateGroupFailure = (error) => {
    toast.error(error?.message)
  };

  const setAsSubmitted = () => setIsSubmitting(false);

  const onSubmit = () => {
    const errorMsgs = validateGroup(group, errors);
    if (Object.keys(removeEmptyProperties(errorMsgs)).length >= 1) {
      setErrors(errorMsgs);
      return;
    }
    setIsSubmitting(true);
    const groupDTO = makeEditGroupDTO(group);
    updateGroup(groupDTO, groupId)
      .then(handleUpdateGroupSuccess)
      .catch(handleUpdateGroupFailure)
      .finally(setAsSubmitted);
  };

  const onCancel = () => {
    if (areFormValuesChanged(group)) {
      openCancelConfirmationDialog()
    }
  }

  const handleSubmitCancelConfirmationDialog = () => {
    const initialGroup = getInitialGroup(groupObj);
    setGroup(initialGroup);
    setErrors({});
    closeCancelConfirmationDialog()
    navigate(`/view-group/${groupId}`)
  }

  if (isGroupLoading) {
    return <LoadingSpinner />
  }

  const areGroupValuesChanged = areFormValuesChanged(groupObj, group)

  const breadcrumbPath = [
    { label: 'groups-edit-tab', path: '/search-group' },
    { label: `${group?.groupName}`, path: `/view-group/${groupId}` },
    { label: 'view-group-edit-button' }
  ]

  return (
    <>
      {!isMobileView && (
      <div
        style={{ backgroundColor: '#F5F5F5', width: '100vw', padding: '20px' }}
      >

        <Breadcrumbs crumbs={breadcrumbPath} />
      </div>
            )}
      <GroupForm
        group={group}
        setGroup={setGroup}
        errors={errors}
        setErrors={setErrors}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        formFieldsConfig={EDIT_FORM_FIELDS_CONFIG}
        primaryButtonLabel={t('edit-user-update-button')}
        areFormValuesChanged={areGroupValuesChanged}
        isEditPage
      />
      <ConfirmationDialog
        showDialog={isCancelConfirmationDialogOpen}
        onSubmit={handleSubmitCancelConfirmationDialog}
        onCancel={closeCancelConfirmationDialog}
      />
    </>
  );
}

export default EditGroup;
